'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Send, Zap, CheckCircle, AlertCircle, Settings, Play, RefreshCw, Phone, Shield, Users, ChevronDown, ChevronUp, Save, Database, Edit2, FileText, Loader2, ListFilter, X, Clock, CalendarClock } from 'lucide-react';
import { WABA_TEMPLATES, DRIP_SEQUENCES, buildMessage } from '@/lib/twilio';
import { toast } from 'sonner';

import { createClient } from '@/lib/supabase/client';

type TabType = 'overview' | 'templates' | 'drip' | 'settings' | 'template_sids';

interface SendResult {
  success: boolean;
  messageSid?: string;
  error?: string;
  preview?: string;
}

interface TemplateSidRow {
  id: string;
  template_key: string;
  content_sid: string | null;
  label: string | null;
  updated_at: string;
}

interface WaTemplate {
  id: string;
  name: string;
  label: string;
  body: string;
  category: string;
  language: string;
  status: string;
  uses: number;
  content_sid?: string | null;
  twilio_approval_status?: string | null;
}

const TEST_LEAD = { name: 'Arjun Mehta', phone: '+919876543210', neetScore: 612, stage: 'New', source: 'Website' };

interface TwilioSettingsClientProps {
  isConfigured?: boolean;
}

// ── Bulk Enroll Modal ──────────────────────────────────────────────────────────
interface BulkEnrollModalProps {
  open: boolean;
  onClose: () => void;
  dripSequences: Record<string, { name: string; steps: { day: number; template: string; description: string }[] }>;
  supabase: ReturnType<typeof createClient>;
}

const LEAD_SOURCES = ['MBBS Lighthouse', 'GMC', 'Neet Navigator'] as const;
const LEAD_STAGES = ['New', 'Contacted', 'Interested', 'Counseling Scheduled', 'Applied', 'Converted', 'Lost'] as const;

function BulkEnrollModal({ open, onClose, dripSequences, supabase }: BulkEnrollModalProps) {
  const [filterType, setFilterType] = useState<'all' | 'source' | 'stage'>('all');
  const [filterSource, setFilterSource] = useState<string>(LEAD_SOURCES[0]);
  const [filterStage, setFilterStage] = useState<string>(LEAD_STAGES[0]);
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [matchedCount, setMatchedCount] = useState<number | null>(null);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollResult, setEnrollResult] = useState<{ enrolled: number; failed: number; firstError?: string } | null>(null);

  // Set default campaign when sequences load
  useEffect(() => {
    const keys = Object.keys(dripSequences);
    if (keys.length > 0 && !selectedCampaign) {
      setSelectedCampaign(keys[0]);
    }
  }, [dripSequences, selectedCampaign]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setMatchedCount(null);
      setEnrollResult(null);
      setFilterType('all');
    }
  }, [open]);

  const fetchMatchedCount = useCallback(async () => {
    setIsCountLoading(true);
    try {
      let query = supabase.from('leads').select('id', { count: 'exact', head: true }).or('opted_in.eq.true,opted_in.is.null').not('phone', 'is', null).neq('phone', '');
      if (filterType === 'source') query = query.eq('source', filterSource);
      if (filterType === 'stage') query = query.eq('stage', filterStage);
      const { count, error } = await query;
      if (error) throw error;
      setMatchedCount(count ?? 0);
    } catch {
      toast.error('Failed to count leads');
    } finally {
      setIsCountLoading(false);
    }
  }, [supabase, filterType, filterSource, filterStage]);

  // Auto-fetch count when filters change
  useEffect(() => {
    if (open) fetchMatchedCount();
  }, [open, fetchMatchedCount]);

  const handleBulkEnroll = async () => {
    if (!selectedCampaign) {
      toast.error('Please select a drip sequence');
      return;
    }
    if (matchedCount === 0) {
      toast.error('No leads match the selected filter');
      return;
    }

    setIsEnrolling(true);
    setEnrollResult(null);
    try {
      // Fetch matching leads (name, phone, neet_score)
      let query = supabase
        .from('leads')
        .select('name, phone, neet_score, stage, source')
        .or('opted_in.eq.true,opted_in.is.null')
        .not('phone', 'is', null)
        .neq('phone', '');
      if (filterType === 'source') query = query.eq('source', filterSource);
      if (filterType === 'stage') query = query.eq('stage', filterStage);

      const { data: leads, error } = await query.limit(500);
      if (error) throw error;
      if (!leads || leads.length === 0) {
        toast.error('No leads found for the selected filter');
        return;
      }

      // Call drip API
      const res = await fetch('/api/twilio/drip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignType: selectedCampaign,
          leads: leads.map((l) => ({
            name: l.name,
            phone: l.phone,
            neetScore: l.neet_score ?? undefined,
            stage: l.stage,
            source: l.source,
          })),
          sendImmediately: true,
        }),
      });

      const data = await res.json() as { success: boolean; enrolledLeads?: number; immediateResults?: Array<{ success: boolean }>; firstError?: string; uniqueErrors?: string[]; error?: string; queuedMessages?: number; note?: string };

      if (data.success) {
        const enrolled = data.enrolledLeads ?? leads.length;
        const queued = data.queuedMessages ?? 0;
        let failed = data.firstError ? 1 : 0;
        setEnrollResult({ enrolled, failed, firstError: data.firstError ?? undefined });
        if (enrolled > 0) {
          toast.success(`Bulk enrollment queued! ${enrolled} leads enrolled. ${queued} messages scheduled.`);
        } else {
          toast.error('Bulk enrollment failed. See error details below.');
        }
      } else {
        toast.error(data.error ?? 'Bulk enrollment failed');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Network error';
      toast.error(msg);
    } finally {
      setIsEnrolling(false);
    }
  };

  if (!open) return null;

  const campaignKeys = Object.keys(dripSequences);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <ListFilter size={16} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground">Bulk Enroll Leads</h2>
              <p className="text-xs text-muted-foreground">Filter leads and enroll them all at once</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted transition-colors text-muted-foreground"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Step 1: Filter */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Step 1 — Select Leads</p>
            <div className="flex gap-2 mb-3">
              {(['all', 'source', 'stage'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors capitalize ${
                    filterType === type
                      ? 'bg-primary text-white border-primary' :'bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30'
                  }`}
                >
                  {type === 'all' ? 'All Leads' : `By ${type.charAt(0).toUpperCase() + type.slice(1)}`}
                </button>
              ))}
            </div>

            {filterType === 'source' && (
              <select
                value={filterSource}
                onChange={(e) => setFilterSource(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {LEAD_SOURCES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}

            {filterType === 'stage' && (
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {LEAD_STAGES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}

            {/* Matched count */}
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 border border-border/50">
              {isCountLoading ? (
                <Loader2 size={13} className="animate-spin text-muted-foreground" />
              ) : (
                <Users size={13} className="text-primary" />
              )}
              <span className="text-xs text-muted-foreground">
                {isCountLoading
                  ? 'Counting leads…'
                  : matchedCount === null
                  ? 'Loading…'
                  : (
                    <>
                      <span className="font-bold text-foreground">{matchedCount.toLocaleString()}</span> opted-in leads match
                      {filterType === 'source' && <span className="text-primary"> · Source: {filterSource}</span>}
                      {filterType === 'stage' && <span className="text-primary"> · Stage: {filterStage}</span>}
                    </>
                  )}
              </span>
            </div>
          </div>

          {/* Step 2: Pick sequence */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Step 2 — Pick Drip Sequence</p>
            {campaignKeys.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">No drip sequences found. Create one in Campaign Management.</p>
            ) : (
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {campaignKeys.map((key) => (
                  <option key={key} value={key}>{dripSequences[key].name}</option>
                ))}
              </select>
            )}
            {selectedCampaign && dripSequences[selectedCampaign] && (
              <p className="text-xs text-muted-foreground mt-1.5">
                {dripSequences[selectedCampaign].steps.length} steps ·{' '}
                {dripSequences[selectedCampaign].steps[dripSequences[selectedCampaign].steps.length - 1]?.day ?? 0} day sequence ·{' '}
                Day 0 message sends immediately
              </p>
            )}
          </div>

          {/* Result */}
          {enrollResult && (
            <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
              enrollResult.failed === 0
                ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
              <CheckCircle size={15} className="shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">
                  {enrollResult.enrolled} leads enrolled successfully
                  {enrollResult.failed > 0 && ` · ${enrollResult.failed} failed`}
                </p>
                {enrollResult.firstError ? (
                  <p className="text-xs mt-1 font-mono bg-black/10 rounded px-2 py-1 break-all">
                    Error: {enrollResult.firstError}
                  </p>
                ) : (
                  <p className="text-xs mt-0.5 opacity-80">All messages queued. Day 0 will be sent within minutes; follow-up messages scheduled automatically.</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/20 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-muted transition-colors"
          >
            {enrollResult ? 'Close' : 'Cancel'}
          </button>
          {!enrollResult && (
            <button
              onClick={handleBulkEnroll}
              disabled={isEnrolling || isCountLoading || matchedCount === 0 || !selectedCampaign}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 active:scale-95"
            >
              {isEnrolling ? (
                <><RefreshCw size={14} className="animate-spin" /> Enrolling…</>
              ) : (
                <><Play size={14} /> Enroll {matchedCount !== null && matchedCount > 0 ? `${matchedCount.toLocaleString()} Leads` : 'Leads'}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
// ── End BulkEnrollModal ────────────────────────────────────────────────────────

export default function TwilioSettingsClient({ isConfigured = false }: TwilioSettingsClientProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [testPhone, setTestPhone] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);
  const [expandedDrip, setExpandedDrip] = useState<string | null>('neet-600-india');
  const [enrollCampaign, setEnrollCampaign] = useState('');
  const [enrollPhone, setEnrollPhone] = useState('');
  const [enrollName, setEnrollName] = useState('');
  const [enrollScore, setEnrollScore] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isBulkEnrollOpen, setIsBulkEnrollOpen] = useState(false);

  // Queue scheduler state
  interface QueueStats { pending: number; sent: number; failed: number; total: number; }
  interface NextScheduled { scheduled_at: string; campaign_id: string; drip_day: number; }
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [nextScheduled, setNextScheduled] = useState<NextScheduled | null>(null);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [cancellingQueue, setCancellingQueue] = useState(false);

  // Template SID state
  const [templateSids, setTemplateSids] = useState<Record<string, TemplateSidRow>>({});
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [loadingSids, setLoadingSids] = useState(false);

  // Supabase templates state (for Templates tab)
  const [waTemplates, setWaTemplates] = useState<WaTemplate[]>([]);
  const [loadingWaTemplates, setLoadingWaTemplates] = useState(false);
  const [syncingStatuses, setSyncingStatuses] = useState(false);

  // Drip sequences from Supabase
  interface DbDripStep { campaign_id: string; campaign_name: string; step_order: number; day: number; template: string; description: string; }
  const [dbDripSequences, setDbDripSequences] = useState<Record<string, { name: string; steps: { day: number; template: string; description: string }[] }>>({});
  const [loadingDrip, setLoadingDrip] = useState(false);

  // Set default enrollCampaign when dbDripSequences loads
  useEffect(() => {
    const keys = Object.keys(dbDripSequences);
    if (keys.length > 0 && !enrollCampaign) {
      setEnrollCampaign(keys[0]);
    }
  }, [dbDripSequences, enrollCampaign]);

  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const loadTemplateSids = useCallback(async () => {
    setLoadingSids(true);
    try {
      const { data, error } = await supabase
        .from('twilio_template_sids')
        .select('*')
        .order('template_key')
        .limit(1000);
      if (error) throw error;
      const map: Record<string, TemplateSidRow> = {};
      const vals: Record<string, string> = {};
      (data ?? []).forEach((row: TemplateSidRow) => {
        map[row.template_key] = row;
        vals[row.template_key] = row.content_sid ?? '';
      });
      setTemplateSids(map);
      setEditValues(vals);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load template SIDs';
      toast.error(msg);
    } finally {
      setLoadingSids(false);
    }
  }, [supabase]);

  const fetchWaTemplates = useCallback(async () => {
    setLoadingWaTemplates(true);
    try {
      const { data, error } = await supabase
        .from('whatsapp_templates')
        .select('*')
        .order('name', { ascending: true })
        .limit(1000);
      if (error) throw error;
      const templates = data ?? [];
      setWaTemplates(templates);
      // Auto-select first approved template
      const firstApproved = templates.find((t: WaTemplate) => t.status === 'Approved');
      if (firstApproved) setSelectedTemplate(firstApproved.name);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load templates';
      toast.error(msg);
    } finally {
      setLoadingWaTemplates(false);
    }
  }, [supabase]);

  const syncAllStatuses = useCallback(async () => {
    const templatesWithSid = waTemplates.filter((t) => t.content_sid && t.id);
    if (templatesWithSid.length === 0) {
      toast.info('No submitted templates to sync (templates need a Content SID).');
      return;
    }
    setSyncingStatuses(true);
    let updated = 0;
    let failed = 0;
    for (const tpl of templatesWithSid) {
      try {
        const res = await fetch(
          `/api/twilio/template-approval?templateId=${encodeURIComponent(tpl.id)}&contentSid=${encodeURIComponent(tpl.content_sid!)}`,
          { cache: 'no-store' }
        );
        if (res.ok) updated++;
        else failed++;
      } catch {
        failed++;
      }
    }
    setSyncingStatuses(false);
    if (failed === 0) {
      toast.success(`Synced status for ${updated} template${updated !== 1 ? 's' : ''}.`);
    } else {
      toast.warning(`Synced ${updated} template${updated !== 1 ? 's' : ''}; ${failed} failed.`);
    }
    fetchWaTemplates();
  }, [waTemplates, fetchWaTemplates]);

  const fetchDripSequences = useCallback(async () => {
    setLoadingDrip(true);
    try {
      const { data, error } = await supabase
        .from('campaign_drip_sequences')
        .select('*')
        .order('campaign_id')
        .order('step_order');
      if (error) throw error;
      const grouped: Record<string, { name: string; steps: { day: number; template: string; description: string }[] }> = {};
      (data ?? []).forEach((row: DbDripStep) => {
        if (!grouped[row.campaign_id]) {
          grouped[row.campaign_id] = { name: row.campaign_name, steps: [] };
        }
        grouped[row.campaign_id].steps.push({ day: row.day, template: row.template, description: row.description });
      });
      setDbDripSequences(grouped);
    } catch {
      // silently fall back to empty
    } finally {
      setLoadingDrip(false);
    }
  }, [supabase]);

  const fetchQueueStats = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const res = await fetch('/api/twilio/process-queue');
      const data = await res.json() as { counts: QueueStats; nextScheduled: NextScheduled | null };
      setQueueStats(data.counts ?? null);
      setNextScheduled(data.nextScheduled ?? null);
    } catch {
      // silently ignore
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  const handleProcessQueue = async () => {
    setProcessingQueue(true);
    try {
      const res = await fetch('/api/twilio/process-queue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'manual' })
      });
      const data = await res.json() as { processed: number; sent: number; failed: number };
      if (data.processed === 0) {
        toast.success('No due messages at this time');
      } else {
        toast.success(`Processed ${data.processed} messages — ${data.sent} sent, ${data.failed} failed`);
      }
      await fetchQueueStats();
    } catch {
      toast.error('Failed to process queue');
    } finally {
      setProcessingQueue(false);
    }
  };

  const handleCancelAllPending = async () => {
    if (!confirm('Cancel all pending scheduled messages? They will be marked as skipped and will NOT be sent.')) return;
    setCancellingQueue(true);
    try {
      const supabaseClient = createClient();
      const { error, count } = await supabaseClient
        .from('drip_message_queue')
        .update({ status: 'skipped', processed_at: new Date().toISOString() })
        .eq('status', 'pending')
        .select('id', { count: 'exact', head: true });
      if (error) throw error;
      toast.success(`Cancelled ${count ?? 0} pending messages`);
      await fetchQueueStats();
    } catch {
      toast.error('Failed to cancel pending messages');
    } finally {
      setCancellingQueue(false);
    }
  };

  useEffect(() => {
    fetchWaTemplates();
    loadTemplateSids();
    fetchDripSequences();
    fetchQueueStats();
  }, [fetchWaTemplates, loadTemplateSids, fetchDripSequences, fetchQueueStats]);

  const handleSaveSid = async (templateKey: string) => {
    const newSid = editValues[templateKey]?.trim() ?? '';
    if (newSid && !newSid.startsWith('HX')) {
      toast.error('Content SID must start with HX');
      return;
    }
    setSavingKey(templateKey);
    try {
      const existing = templateSids[templateKey];
      if (existing) {
        const { error } = await supabase
          .from('twilio_template_sids')
          .update({ content_sid: newSid || null, updated_at: new Date().toISOString() })
          .eq('template_key', templateKey);
        if (error) throw error;
      } else {
        const templateLabel = waTemplates.find((t) => t.name === templateKey)?.label ?? templateKey;
        const { error } = await supabase
          .from('twilio_template_sids')
          .insert({
            template_key: templateKey,
            content_sid: newSid || null,
            label: templateLabel,
          });
        if (error) throw error;
      }
      toast.success(`Saved SID for ${templateKey}`);
      await loadTemplateSids();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    } finally {
      setSavingKey(null);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    setIsSending(true);
    setLastResult(null);
    try {
      const res = await fetch('/api/twilio/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send_template',
          templateKey: selectedTemplate,
          lead: { ...TEST_LEAD, phone: testPhone },
        }),
      });
      const data = await res.json() as SendResult;
      setLastResult(data);
      if (data.success) {
        toast.success(`Message sent! SID: ${data.messageSid}`);
      } else {
        toast.error(data.error ?? 'Failed to send message');
      }
    } catch {
      toast.error('Network error — check your connection');
    } finally {
      setIsSending(false);
    }
  };

  const handleEnrollDrip = async () => {
    if (!enrollPhone.trim() || !enrollName.trim()) {
      toast.error('Please enter name and phone number');
      return;
    }
    setIsEnrolling(true);
    try {
      const res = await fetch('/api/twilio/drip', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignType: enrollCampaign,
          leads: [{
            name: enrollName,
            phone: enrollPhone,
            neetScore: enrollScore ? parseInt(enrollScore) : undefined,
          }],
          sendImmediately: true,
        }),
      });
      const data = await res.json() as { success: boolean; enrolledLeads?: number; error?: string };
      if (data.success) {
        toast.success(`Lead enrolled in drip campaign! Day 0 message sent.`);
        setEnrollPhone('');
        setEnrollName('');
        setEnrollScore('');
      } else {
        toast.error(data.error ?? 'Enrollment failed');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setIsEnrolling(false);
    }
  };

  const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: Settings },
    { id: 'templates', label: 'Templates', icon: MessageSquare },
    { id: 'drip', label: 'Drip Sequences', icon: Zap },
    { id: 'template_sids', label: 'Template SIDs', icon: Database },
    { id: 'settings', label: 'Test & Config', icon: Phone },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Twilio WhatsApp Integration</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            WhatsApp Business API · Drip Campaigns · Two-Way Messaging
          </p>
        </div>
        {isConfigured ? (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold bg-green-50 text-green-700 border-green-200">
            <CheckCircle size={13} />
            Live &amp; Active
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold bg-amber-50 text-amber-700 border-amber-200">
            <AlertCircle size={13} />
            Credentials Required
          </div>
        )}
      </div>

      {/* Config Banner */}
      {isConfigured ? null : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">Twilio credentials not yet configured</p>
            <p className="text-xs text-amber-700 mt-1">
              Add <code className="bg-amber-100 px-1 rounded">TWILIO_ACCOUNT_SID</code>,{' '}
              <code className="bg-amber-100 px-1 rounded">TWILIO_AUTH_TOKEN</code>, and{' '}
              <code className="bg-amber-100 px-1 rounded">TWILIO_WHATSAPP_NUMBER</code> to your{' '}
              <code className="bg-amber-100 px-1 rounded">.env</code> file to activate live messaging.
              Use <strong>whatsapp:+14155238886</strong> for Twilio Sandbox testing.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <TabIcon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'API Routes', value: '3', sub: '/send · /webhook · /drip', icon: Send, color: 'text-primary' },
              { label: 'Templates', value: String(Object.keys(WABA_TEMPLATES).length), sub: 'Pre-approved WABA', icon: MessageSquare, color: 'text-green-600' },
              { label: 'Drip Sequences', value: String(Object.keys(DRIP_SEQUENCES).length), sub: 'Campaign types', icon: Zap, color: 'text-accent' },
              { label: 'Webhook', value: 'Ready', sub: '/api/twilio/webhook', icon: RefreshCw, color: 'text-blue-600' },
            ].map((stat) => {
              const StatIcon = stat.icon;
              return (
                <div key={stat.label} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <StatIcon size={16} className={stat.color} />
                    <span className="text-xs text-muted-foreground">{stat.label}</span>
                  </div>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
                </div>
              );
            })}
          </div>

          {/* Architecture */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-base font-semibold text-foreground mb-4">Integration Architecture</h3>
            <div className="space-y-3">
              {[
                {
                  title: 'Outbound Messaging',
                  desc: 'POST /api/twilio/send — Send individual messages, template messages, or stage-triggered messages to leads',
                  icon: Send,
                  color: 'bg-primary/10 text-primary',
                },
                {
                  title: 'Inbound Webhook',
                  desc: 'POST /api/twilio/webhook — Receives incoming WhatsApp replies, handles STOP/START opt-out/opt-in keywords',
                  icon: RefreshCw,
                  color: 'bg-green-100 text-green-700',
                },
                {
                  title: 'Drip Campaign Enrollment',
                  desc: 'POST /api/twilio/drip — Enroll leads into multi-day sequences. Day 0 sends immediately; subsequent days require a scheduler.',
                  icon: Zap,
                  color: 'bg-accent/10 text-accent',
                },
                {
                  title: 'Stage-Based Triggers',
                  desc: 'Automatic WhatsApp messages fire when a lead\'s stage changes (New → Contacted → Interested → Counseling Scheduled)',
                  icon: Play,
                  color: 'bg-blue-100 text-blue-700',
                },
                {
                  title: 'Opt-in / Compliance',
                  desc: 'STOP keyword triggers opt-out. START re-subscribes. All messages use pre-approved WABA templates.',
                  icon: Shield,
                  color: 'bg-purple-100 text-purple-700',
                },
              ].map((item) => {
                const ItemIcon = item.icon;
                return (
                  <div key={item.title} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${item.color}`}>
                      <ItemIcon size={15} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{item.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Webhook URL */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-base font-semibold text-foreground mb-3">Twilio Console Configuration</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Webhook URL (Incoming Messages)</p>
                <div className="flex items-center gap-2 bg-muted rounded-lg px-3 py-2">
                  <code className="text-sm text-foreground flex-1 break-all">
                    https://neetnaviga9114.builtwithrocket.new/api/twilio/webhook
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('https://neetnaviga9114.builtwithrocket.new/api/twilio/webhook');
                      toast.success('Copied!');
                    }}
                    className="text-xs text-primary font-medium hover:underline shrink-0"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">HTTP Method</p>
                <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded">POST</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Set this URL in your Twilio Console under{' '}
                <strong>Messaging → WhatsApp Senders → [Your Number] → A message comes in</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div>
          {loadingWaTemplates ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-primary" />
            </div>
          ) : waTemplates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <MessageSquare size={32} className="text-muted-foreground mb-3" />
              <p className="text-sm font-semibold text-foreground mb-1">No templates yet</p>
              <p className="text-xs text-muted-foreground">Create templates in the Campaign Management section.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-foreground">
                  Template Library
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    {waTemplates.filter((t) => t.status === 'Approved').length} approved ·{' '}
                    {waTemplates.filter((t) => !t.content_sid).length} pending submission
                  </span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={syncAllStatuses}
                    disabled={syncingStatuses || loadingWaTemplates}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {syncingStatuses ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RefreshCw size={12} />
                    )}
                    {syncingStatuses ? 'Syncing…' : 'Sync Status'}
                  </button>
                  <button
                    onClick={fetchWaTemplates}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    <RefreshCw size={12} />
                    Refresh
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {waTemplates.map((tpl) => (
                  <div key={tpl.id} className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText size={16} className="text-primary" />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          tpl.status === 'Approved' ? 'bg-green-50 text-green-700' :
                          tpl.status === 'Rejected' ? 'bg-red-50 text-red-700' :
                          tpl.status === 'Paused'|| tpl.status === 'Disabled' ? 'bg-gray-100 text-gray-600' : 'bg-amber-50 text-amber-700'
                        }`}>
                          {tpl.status}
                        </span>
                        {tpl.content_sid && (
                          <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[100px]" title={tpl.content_sid}>
                            {tpl.content_sid.slice(0, 10)}…
                          </span>
                        )}
                      </div>
                    </div>
                    <h4 className="font-semibold text-foreground text-sm mb-1">{tpl.label}</h4>
                    <p className="text-xs text-muted-foreground mb-1 font-mono">{tpl.name}</p>
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-3 flex-1">{tpl.body}</p>
                    <div className="flex items-center justify-between border-t border-border/50 pt-2 mt-auto">
                      <span className="text-xs text-muted-foreground">{tpl.category}</span>
                      {tpl.status === 'Approved' && (
                        <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                          <CheckCircle size={11} />Ready to use
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Drip Sequences Tab */}
      {activeTab === 'drip' && (
        <div className="space-y-4">
          {/* Scheduler Status Panel */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <CalendarClock size={16} className="text-primary" />
                Drip Scheduler Status
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchQueueStats}
                  disabled={loadingQueue}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  <RefreshCw size={12} className={loadingQueue ? 'animate-spin' : ''} />
                  Refresh
                </button>
                <button
                  onClick={handleProcessQueue}
                  disabled={processingQueue}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                >
                  {processingQueue ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
                  Run Now
                </button>
                <button
                  onClick={handleCancelAllPending}
                  disabled={cancellingQueue || (queueStats?.pending ?? 0) === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancellingQueue ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                  Cancel All
                </button>
              </div>
            </div>

            {loadingQueue ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={20} className="animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4">
                {/* Stats row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Pending', value: queueStats?.pending ?? 0, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
                    { label: 'Sent', value: queueStats?.sent ?? 0, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
                    { label: 'Failed', value: queueStats?.failed ?? 0, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
                    { label: 'Total Queued', value: queueStats?.total ?? 0, color: 'text-primary', bg: 'bg-primary/5 border-primary/20' },
                  ].map((stat) => (
                    <div key={stat.label} className={`rounded-lg border px-4 py-3 ${stat.bg}`}>
                      <p className={`text-xl font-bold ${stat.color}`}>{stat.value.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Next scheduled */}
                {nextScheduled ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-muted/40 border border-border/50 text-xs">
                    <Clock size={13} className="text-primary shrink-0" />
                    <span className="text-muted-foreground">Next message:</span>
                    <span className="font-semibold text-foreground">
                      {new Date(nextScheduled.scheduled_at).toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">Campaign <span className="font-medium text-foreground">{nextScheduled.campaign_id}</span> · Day {nextScheduled.drip_day}</span>
                  </div>
                ) : queueStats && queueStats.pending === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-muted/40 border border-border/50 text-xs text-muted-foreground">
                    <CheckCircle size={13} className="text-green-600 shrink-0" />
                    No pending messages — queue is clear
                  </div>
                ) : null}

                <p className="text-xs text-muted-foreground">
                  The scheduler runs automatically every hour via pg_cron. Use <strong>Run Now</strong> to process due messages immediately.
                </p>
              </div>
            )}
          </div>

          {/* Enroll Form */}
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Users size={16} className="text-primary" />
                Enroll Lead in Drip Campaign
              </h3>
              <button
                onClick={() => setIsBulkEnrollOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-accent/10 text-accent border border-accent/30 rounded-lg text-xs font-semibold hover:bg-accent/20 transition-colors"
              >
                <ListFilter size={13} />
                Bulk Enroll
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <input
                type="text"
                placeholder="Lead Name"
                value={enrollName}
                onChange={(e) => setEnrollName(e.target.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <input
                type="text"
                placeholder="Phone (+91XXXXXXXXXX)"
                value={enrollPhone}
                onChange={(e) => setEnrollPhone(e.target.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <input
                type="number"
                placeholder="NEET Score (optional)"
                value={enrollScore}
                onChange={(e) => setEnrollScore(e.target.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <select
                value={enrollCampaign}
                onChange={(e) => setEnrollCampaign(e.target.value)}
                className="px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {Object.keys(dbDripSequences).length > 0
                  ? Object.entries(dbDripSequences).map(([key, campaign]) => (
                      <option key={key} value={key}>{campaign.name}</option>
                    ))
                  : Object.keys(DRIP_SEQUENCES).map((key) => (
                      <option key={key} value={key}>{key}</option>
                    ))
                }
              </select>
            </div>
            <button
              onClick={handleEnrollDrip}
              disabled={isEnrolling}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-95"
            >
              {isEnrolling ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
              {isEnrolling ? 'Enrolling...' : 'Enroll & Send Day 0'}
            </button>
          </div>

          {/* Sequence Viewer */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Campaign Drip Sequences</p>
            <button
              onClick={fetchDripSequences}
              disabled={loadingDrip}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border hover:bg-muted transition-colors"
            >
              <RefreshCw size={12} className={loadingDrip ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          {loadingDrip ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-primary" />
            </div>
          ) : Object.keys(dbDripSequences).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Zap size={28} className="text-muted-foreground mb-3 opacity-40" />
              <p className="text-sm font-semibold text-foreground mb-1">No drip sequences found</p>
              <p className="text-xs text-muted-foreground">Create and save drip sequences in Campaign Management.</p>
            </div>
          ) : (
            Object.entries(dbDripSequences).map(([key, campaign]) => (
            <div key={key} className="bg-card border border-border rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedDrip(expandedDrip === key ? null : key)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Zap size={14} className="text-accent" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground">{campaign.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {campaign.steps.length} steps · {campaign.steps[campaign.steps.length - 1]?.day ?? 0} days
                    </p>
                  </div>
                </div>
                {expandedDrip === key ? (
                  <ChevronUp size={16} className="text-muted-foreground" />
                ) : (
                  <ChevronDown size={16} className="text-muted-foreground" />
                )}
              </button>
              {expandedDrip === key && (
                <div className="px-5 pb-5">
                  <div className="relative">
                    <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-border" />
                    <div className="space-y-3">
                      {campaign.steps.map((step, idx) => (
                        <div key={`${key}-day${step.day}-${idx}`} className="flex items-start gap-3 relative">
                          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0 z-10">
                            D{step.day}
                          </div>
                          <div className="flex-1 bg-muted/50 rounded-lg p-3 border border-border/50">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs font-semibold text-primary">{step.template}</p>
                            </div>
                            <p className="text-sm text-foreground">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
          )}
        </div>
      )}

      {/* Template SIDs Tab */}
      {activeTab === 'template_sids' && (
        <div className="space-y-5">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <Database size={18} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Content Template SID Configuration</p>
              <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                Map each WABA template to its approved Twilio Content Template SID (starts with{' '}
                <code className="bg-blue-100 px-1 rounded">HX</code>). These SIDs are stored in the
                database and used automatically when sending messages, bypassing the 24-hour freeform
                window restriction (error 63016). Get SIDs from{' '}
                <strong>Twilio Console → Content Template Builder</strong>.
              </p>
            </div>
          </div>

          {/* SID Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                <Edit2 size={15} className="text-primary" />
                Template SID Mapping
              </h3>
              <button
                onClick={() => { fetchWaTemplates(); loadTemplateSids(); }}
                disabled={loadingSids || loadingWaTemplates}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <RefreshCw size={13} className={loadingSids ? 'animate-spin' : ''} />
                Refresh
              </button>
            </div>

            {loadingSids || loadingWaTemplates ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw size={20} className="animate-spin text-muted-foreground" />
              </div>
            ) : waTemplates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-2">
                <Database size={24} />
                <p className="text-sm">No templates found. Add templates in the Template Library first.</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {waTemplates.map((template) => {
                  const key = template.name;
                  const row = templateSids[key];
                  const currentSid = editValues[key] ?? '';
                  const savedSid = row?.content_sid ?? '';
                  const isDirty = currentSid !== savedSid;
                  const isSaving = savingKey === key;
                  const hasSid = !!savedSid;

                  return (
                    <div key={key} className="px-5 py-4">
                      <div className="flex items-start gap-4">
                        {/* Template info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-foreground font-mono">{key}</p>
                            {hasSid ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                <CheckCircle size={9} />
                                SID Set
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                <AlertCircle size={9} />
                                Not Set
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mb-2">
                            {template.label ?? key}
                          </p>
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="HXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                              value={currentSid}
                              onChange={(e) =>
                                setEditValues((prev) => ({ ...prev, [key]: e.target.value }))
                              }
                              className="flex-1 px-3 py-1.5 text-xs font-mono border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground/50"
                            />
                            <button
                              onClick={() => handleSaveSid(key)}
                              disabled={isSaving || !isDirty}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${
                                isDirty
                                  ? 'bg-primary text-white hover:bg-primary/90 active:scale-95' :'bg-muted text-muted-foreground cursor-default'
                              }`}
                            >
                              {isSaving ? (
                                <RefreshCw size={12} className="animate-spin" />
                              ) : (
                                <Save size={12} />
                              )}
                              {isSaving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          {hasSid && (
                            <p className="text-[10px] text-muted-foreground mt-1 font-mono">
                              Saved: {savedSid}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Usage note */}
          <div className="bg-muted/40 border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">How it works:</strong> When a message is sent, the app
              first checks the database for a stored Content SID for that template. If found, it uses
              Twilio&apos;s Content API (<code className="bg-muted px-1 rounded">ContentSid</code> +{' '}
              <code className="bg-muted px-1 rounded">ContentVariables</code>) which works outside the
              24-hour window. If no SID is stored, it falls back to a plain body message (sandbox only).
              Database SIDs take priority over any <code className="bg-muted px-1 rounded">TWILIO_CONTENT_SID_*</code> env variables.
            </p>
          </div>
        </div>
      )}

      {/* Settings / Test Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">

          {/* Error 63016 Fix Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle size={18} className="text-blue-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Fix for Error 63016 — Outside 24-Hour Window</p>
              <p className="text-xs text-blue-700 mt-1 leading-relaxed">
                WhatsApp blocks freeform messages sent outside the 24-hour customer-initiated window. To send anytime,
                create approved templates in{' '}
                <strong>Twilio Console → Content Template Builder</strong>, then map each template&apos;s SID
                (starts with <code className="bg-blue-100 px-1 rounded">HX</code>) in the{' '}
                <strong>Template SIDs</strong> tab above — no env variables needed.
              </p>
            </div>
          </div>

          {/* Test Message */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Send size={16} className="text-primary" />
              Send Test Message
            </h3>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 block">
                    Recipient Phone (WhatsApp)
                  </label>
                  <input
                    type="text"
                    placeholder="+91XXXXXXXXXX"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1 block">
                    Template
                  </label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {waTemplates.filter((t) => t.status === 'Approved').map((t) => (
                      <option key={t.name} value={t.name}>{t.label || t.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-muted/40 rounded-lg p-3 border border-border/50">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Message Preview</p>
                <p className="text-sm text-foreground leading-relaxed">
                  {waTemplates.find((t) => t.name === selectedTemplate)?.body
                    ? waTemplates.find((t) => t.name === selectedTemplate)!.body
                        .replace(/\{\{name\}\}/gi, TEST_LEAD.name)
                        .replace(/\{\{1\}\}/g, TEST_LEAD.name)
                        .replace(/\{\{score\}\}/gi, String(TEST_LEAD.neetScore))
                        .replace(/\{\{2\}\}/g, String(TEST_LEAD.neetScore))
                        .replace(/\{\{stage\}\}/gi, TEST_LEAD.stage)
                        .replace(/\{\{3\}\}/g, TEST_LEAD.stage)
                    : buildMessage(selectedTemplate, TEST_LEAD)}
                </p>
              </div>

              <button
                onClick={handleSendTest}
                disabled={isSending}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-60 active:scale-95"
              >
                {isSending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                {isSending ? 'Sending...' : 'Send Test Message'}
              </button>

              {lastResult && (
                <div className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
                  lastResult.success
                    ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  {lastResult.success ? <CheckCircle size={16} className="shrink-0 mt-0.5" /> : <AlertCircle size={16} className="shrink-0 mt-0.5" />}
                  <div>
                    {lastResult.success ? (
                      <p>Message sent! SID: <code className="font-mono text-xs">{lastResult.messageSid}</code></p>
                    ) : (
                      <>
                        <p className="font-semibold">Error: {lastResult.error}</p>
                        {lastResult.error?.includes('63016') && (
                          <p className="text-xs mt-1 text-red-700">
                            ➜ Go to the <strong>Template SIDs</strong> tab and add the approved Content SID for <code className="bg-red-100 px-1 rounded">{selectedTemplate}</code> from Twilio Console → Content Template Builder.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Environment Variables */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield size={16} className="text-primary" />
              Required Environment Variables
            </h3>
            <div className="space-y-2">
              {[
                { key: 'TWILIO_ACCOUNT_SID', desc: 'Your Twilio Account SID from console.twilio.com', example: 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx' },
                { key: 'TWILIO_AUTH_TOKEN', desc: 'Your Twilio Auth Token (keep secret)', example: 'your_auth_token_here' },
                { key: 'TWILIO_WHATSAPP_NUMBER', desc: 'Your WhatsApp-enabled Twilio number', example: 'whatsapp:+14155238886' },
              ].map((env) => (
                <div key={env.key} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                  <div className="flex-1">
                    <code className="text-sm font-mono font-bold text-foreground">{env.key}</code>
                    <p className="text-xs text-muted-foreground mt-0.5">{env.desc}</p>
                    <p className="text-xs text-muted-foreground/60 font-mono mt-0.5">e.g. {env.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Add these to your <code className="bg-muted px-1 rounded">.env</code> file. For Twilio Sandbox testing, use{' '}
              <code className="bg-muted px-1 rounded">whatsapp:+14155238886</code> as the number and join the sandbox by sending{' '}
              <strong>join [sandbox-keyword]</strong> from your WhatsApp to that number.
            </p>
          </div>
        </div>
      )}

      {/* Bulk Enroll Modal */}
      <BulkEnrollModal
        open={isBulkEnrollOpen}
        onClose={() => setIsBulkEnrollOpen(false)}
        dripSequences={dbDripSequences}
        supabase={supabase}
      />
    </div>
  );
}
